/**
 * Packing (F7.1, F7.2, F7.4) for the console:
 *   /skus             GET ?cropId&approvedAtLocationId&search · POST · PATCH /:id · DELETE /:id (409 SKU_IN_USE)
 *   /label-versions   GET ?skuId&status&premisesLocationId · POST · PATCH /:id (409 LABEL_FROZEN)
 *                     POST /:id/approve (422 LABEL_NOT_APPROVABLE {problems}) · POST /:id/retire
 *   /packable-batches GET ?skuId&includeIneligible (with reasons) · ?code (one batch with its reasons) → { data: [...] }
 *   /pack-runs        GET ?status&locationId&batchUnitId · GET /:id (+ balance) · POST
 *                     POST /:id/cancel { reason } · POST /:id/start
 *   /pack-lots/:id    GET (counts, reconciliation, checks) · /coder-sheet.pdf · /carton-labels.pdf
 * Rows are raw SQL (snake_case); kg and ₹ as decimal strings; timestamps "YYYY-MM-DD HH:MM:SS+05:30".
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { getList, getOne, getPage, useConsoleMutation, usePageQuery, type PageParams } from './core'
import {
  arr,
  bool,
  decimalToMinor,
  inrToPaise,
  kgToGrams,
  kgToGrams0,
  num,
  numOrNull,
  obj,
  pick,
  str,
  strOrNull,
} from './mappers'
import { mapLocation } from './store'
import type { CreateSkuBody, UpdateSkuBody } from '../model/pack/skuForm'
import type { createLabelBody, updateLabelBody } from '../model/pack/labelForm'
import type { planBody, startBody } from '../model/pack/runForms'

/** Query keys for this area: ['console', 'pack', …]. */
export const pk = {
  all: ['console', 'pack'] as const,
  skus: ['console', 'pack', 'skus'] as const,
  labels: ['console', 'pack', 'labels'] as const,
  packable: ['console', 'pack', 'packable'] as const,
  runs: ['console', 'pack', 'runs'] as const,
  lots: ['console', 'pack', 'lots'] as const,
  halls: ['console', 'pack', 'halls'] as const,
  equipment: ['console', 'pack', 'equipment'] as const,
}

/** "2026-10-10 17:08:36.889+05:30" → ISO-8601 that every browser parses. */
export function ts(value: unknown): string | null {
  const s = strOrNull(value)
  if (!s) return null
  return /^\d{4}-\d{2}-\d{2} \d/.test(s) ? s.replace(' ', 'T') : s
}

// ============================== SKUs ==============================

export interface SkuRow {
  id: string
  code: string
  name: string
  cropId: string
  cropName: string | null
  netG: number
  /** 14 digits as stored. */
  gtin: string | null
  shelfLifeDays: number | null
  /** "15.0" grams. */
  toleranceG: string | null
  pouchesPerCarton: number | null
}
export const mapSku = (r: Record<string, unknown>): SkuRow => ({
  id: str(r.id),
  code: str(r.code),
  name: str(r.name),
  cropId: str(pick(r, 'crop_id', 'cropId')),
  cropName: strOrNull(pick(r, 'crop_name', 'cropName')),
  netG: num(pick(r, 'net_g', 'netG')),
  gtin: strOrNull(r.gtin),
  shelfLifeDays: numOrNull(pick(r, 'shelf_life_days', 'shelfLifeDays')),
  toleranceG: strOrNull(pick(r, 'tolerance_g', 'toleranceG')),
  pouchesPerCarton: numOrNull(pick(r, 'pouches_per_carton', 'pouchesPerCarton')),
})

export interface SkuFilters extends PageParams {
  cropId?: string
  search?: string
  approvedAtLocationId?: string
}
export function useSkus(f: SkuFilters) {
  return usePageQuery([...pk.skus, 'list', f], () => getPage('/skus', f, mapSku))
}
/** Every product (for pickers and names in lists). */
export function useAllSkus() {
  return useQuery({
    queryKey: [...pk.skus, 'all'],
    queryFn: () => getPage('/skus', { page: 1, limit: 500 }, mapSku).then((p) => p.rows),
    staleTime: 5 * 60_000,
  })
}
/** `inline`: the dialog shows the refusal itself. */
export function useSaveSku({ inline = false } = {}) {
  return useConsoleMutation<{ id?: string; body: CreateSkuBody | UpdateSkuBody }>({
    mutationFn: ({ id, body }) => (id ? api.patch(`/skus/${id}`, body) : api.post('/skus', body)),
    invalidate: [pk.skus],
    success: (_d, v) => (v.id ? 'Product saved' : 'Product added'),
    errorTitle: 'Product not saved',
    onError: () => inline,
  })
}
export function useDeleteSku() {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.delete(`/skus/${id}`),
    invalidate: [pk.skus],
    success: 'Product deleted',
    errorTitle: 'Product not deleted',
    onError: () => true,
  })
}

// ============================== label versions ==============================

export interface LabelVersionRow {
  id: string
  skuId: string
  skuCode: string
  skuName: string
  code: string
  status: string
  premisesLocationId: string
  premisesCode: string | null
  premisesName: string | null
  fssaiLicenceNo: string | null
  fssaiLicenceValidTo: string | null
  /** "180.00" as sent. */
  mrpInr: string | null
  mrpPaise: number | null
  allergenStatement: string | null
  claimText: string | null
  claimPanel: string | null
  claimMinAnalytes: number | null
  claimMaxLoq: string | null
  artworkUrl: string | null
  approvedById: string | null
  approvedAt: string | null
  createdAt: string | null
}
export const mapLabelVersion = (r: Record<string, unknown>): LabelVersionRow => ({
  id: str(r.id),
  skuId: str(r.sku_id),
  skuCode: str(r.sku_code),
  skuName: str(r.sku_name),
  code: str(r.code),
  status: str(r.status, 'DRAFT'),
  premisesLocationId: str(r.premises_location_id),
  premisesCode: strOrNull(r.premises_code),
  premisesName: strOrNull(r.premises_name),
  fssaiLicenceNo: strOrNull(r.fssai_licence_no),
  fssaiLicenceValidTo: strOrNull(r.fssai_licence_valid_to)?.slice(0, 10) ?? null,
  mrpInr: strOrNull(r.mrp_inr),
  mrpPaise: inrToPaise(r.mrp_inr as string | null),
  allergenStatement: strOrNull(r.allergen_statement),
  claimText: strOrNull(r.claim_text),
  claimPanel: strOrNull(r.claim_panel),
  claimMinAnalytes: numOrNull(r.claim_min_analytes),
  claimMaxLoq: strOrNull(r.claim_max_loq),
  artworkUrl: strOrNull(r.artwork_url),
  approvedById: strOrNull(r.approved_by_id),
  approvedAt: ts(r.approved_at),
  createdAt: ts(r.created_at),
})

export interface LabelFilters extends PageParams {
  skuId?: string
  status?: string
  premisesLocationId?: string
}
export function useLabelVersions(f: LabelFilters, enabled = true) {
  return usePageQuery(
    [...pk.labels, 'list', f],
    () => getPage('/label-versions', f, mapLabelVersion),
    { enabled },
  )
}
export function useLabelVersion(id: string | null) {
  return useQuery({
    queryKey: [...pk.labels, 'one', id],
    enabled: Boolean(id),
    retry: false,
    queryFn: () => getOne(`/label-versions/${id}`, mapLabelVersion),
  })
}
/** The approved label(s) of a product at a hall (for planning and starting). */
export function useApprovedLabels(skuId: string | null, hallId: string | null) {
  return useQuery({
    queryKey: [...pk.labels, 'approved', skuId, hallId],
    enabled: Boolean(skuId && hallId),
    queryFn: () =>
      getPage(
        '/label-versions',
        { skuId, premisesLocationId: hallId, status: 'APPROVED', page: 1, limit: 20 },
        mapLabelVersion,
      ).then((p) => p.rows),
  })
}
export function useSaveLabel() {
  return useConsoleMutation<
    | { id: string; body: ReturnType<typeof updateLabelBody> }
    | { id?: undefined; body: ReturnType<typeof createLabelBody> },
    LabelVersionRow
  >({
    mutationFn: async ({ id, body }) =>
      mapLabelVersion(
        obj(
          id
            ? await api.patch(`/label-versions/${id}`, body)
            : await api.post('/label-versions', body),
        ),
      ),
    invalidate: [pk.labels],
    success: (_d, v) => (v.id ? 'Draft saved' : 'Draft label added'),
    onError: () => true,
  })
}
export function useApproveLabel() {
  return useConsoleMutation<string, LabelVersionRow>({
    mutationFn: async (id) => mapLabelVersion(obj(await api.post(`/label-versions/${id}/approve`))),
    invalidate: [pk.labels, pk.skus],
    success: (d) => `${d.code} approved — the earlier version at this hall is retired`,
    onError: () => true,
  })
}
export function useRetireLabel() {
  return useConsoleMutation<{ id: string; draft: boolean }>({
    mutationFn: ({ id }) => api.post(`/label-versions/${id}/retire`),
    invalidate: [pk.labels, pk.skus],
    success: (_d, v) => (v.draft ? 'Draft withdrawn' : 'Label retired'),
    errorTitle: 'Not changed',
  })
}

// ============================== packable batches ==============================

export interface PackableBatch {
  unitId: string
  code: string
  eligible: boolean
  reasons: string[]
  cropId: string | null
  seasonCode: string | null
  availableG: number
  sacks: number
  sacksLocationCodes: string[]
  labStatus: string | null
  labUnitId: string | null
  leftoverOfBatchCode: string | null
  parentLotCodes: string[]
}
export const mapPackableBatch = (r: Record<string, unknown>): PackableBatch => ({
  unitId: str(r.unit_id),
  code: str(r.code),
  eligible: bool(r.eligible),
  reasons: arr<unknown>(r.reasons).map(String),
  cropId: strOrNull(r.crop_id),
  seasonCode: strOrNull(r.season_code),
  availableG: kgToGrams0(r.available_kg as string),
  sacks: num(r.sacks),
  sacksLocationCodes: arr<unknown>(r.sacks_location_codes).map(String),
  labStatus: strOrNull(r.lab_status),
  labUnitId: strOrNull(r.lab_unit_id),
  leftoverOfBatchCode: strOrNull(r.leftover_of_batch_code),
  parentLotCodes: arr<unknown>(r.parent_lot_codes).map(String),
})
/** Batches of the product's crop: the packable ones and, with their reasons, those that cannot be packed now. */
export function usePackableBatches(skuId: string | null) {
  return useQuery({
    queryKey: [...pk.packable, 'sku', skuId],
    enabled: Boolean(skuId),
    queryFn: () =>
      getList('/packable-batches', { skuId, includeIneligible: true }, mapPackableBatch),
  })
}
/** One batch by code, with the reasons it cannot be packed. */
export function lookupPackableBatch(code: string): Promise<PackableBatch | null> {
  return getList('/packable-batches', { code: code.trim() }, mapPackableBatch).then(
    (l) => l[0] ?? null,
  )
}
export function usePackableBatchByCode(code: string) {
  return useQuery({
    queryKey: [...pk.packable, 'code', code],
    enabled: code.trim().length >= 3,
    retry: false,
    queryFn: () => lookupPackableBatch(code),
  })
}

// ============================== pack runs ==============================

export interface PackRunRow {
  id: string
  code: string
  status: string
  locationId: string
  locationCode: string | null
  equipmentId: string | null
  equipmentCode: string | null
  operatorId: string | null
  operatorName: string | null
  batchUnitId: string | null
  batchCode: string | null
  skuId: string | null
  plannedG: number | null
  plannedPouches: number | null
  plannedDate: string | null
  labelVersionId: string | null
  cleanoutPhotoId: string | null
  allergenCleanRequired: boolean | null
  allergenCleanConfirmed: boolean | null
  leftoverG: number | null
  scaleCheck: {
    equipmentId: string | null
    referenceG: number | null
    readingG: number | null
  } | null
  startedAt: string | null
  endedAt: string | null
  inputG: number | null
  outputG: number | null
  cleanoutById: string | null
  createdAt: string | null
  packLotId: string | null
  packLotCode: string | null
  packLotStatus: string | null
  packLotHold: string | null
}
export const mapPackRun = (r: Record<string, unknown>): PackRunRow => {
  const p = obj(r.params)
  const sc = p.scale_check ? obj(p.scale_check) : null
  const ac = p.allergen_clean ? obj(p.allergen_clean) : null
  return {
    id: str(r.id),
    code: str(r.code),
    status: str(r.status, 'OPEN'),
    locationId: str(r.location_id),
    locationCode: strOrNull(r.location_code),
    equipmentId: strOrNull(r.equipment_id),
    equipmentCode: strOrNull(r.equipment_code),
    operatorId: strOrNull(r.operator_id),
    operatorName: strOrNull(r.operator_name),
    batchUnitId: strOrNull(r.origin_unit_id),
    batchCode: strOrNull(r.batch_code),
    skuId: strOrNull(p.sku_id),
    plannedG: kgToGrams(p.planned_kg as string | null),
    plannedPouches: numOrNull(p.planned_pouches),
    plannedDate: strOrNull(p.planned_date)?.slice(0, 10) ?? null,
    labelVersionId: strOrNull(p.label_version_id),
    cleanoutPhotoId: strOrNull(p.cleanout_photo_id),
    allergenCleanRequired: ac ? bool(ac.required) : null,
    allergenCleanConfirmed: ac ? bool(ac.confirmed) : null,
    leftoverG: kgToGrams(p.leftover_kg as string | null),
    scaleCheck: sc
      ? {
          equipmentId: strOrNull(sc.equipment_id),
          referenceG: numOrNull(sc.reference_g),
          readingG: numOrNull(sc.reading_g),
        }
      : null,
    startedAt: ts(r.started_at),
    endedAt: ts(r.ended_at),
    inputG: kgToGrams(r.input_kg as string | null),
    outputG: kgToGrams(r.output_kg as string | null),
    cleanoutById: strOrNull(r.cleanout_by_id),
    createdAt: ts(r.created_at),
    packLotId: strOrNull(r.pack_lot_id),
    packLotCode: strOrNull(r.pack_lot_code),
    packLotStatus: strOrNull(r.pack_lot_status),
    packLotHold: strOrNull(r.pack_lot_hold),
  }
}

export interface RunBalance {
  inputG: number
  /** Pouches packed (the PACK step's product). */
  packedG: number
  /** Leftover, spillage and rejects. */
  otherG: number
  outputG: number
  residualG: number
  /** "0.26" — share of the input not accounted for. */
  residualPct: string | null
  lossLimitPct: string | null
  /** |unaccounted| within the loss limit; null when it cannot be judged. */
  withinLimit: boolean | null
  checkedAt: string | null
}
export const mapRunBalance = (b: Record<string, unknown>): RunBalance => {
  const inputG = kgToGrams0(b.input_kg as string)
  const outputG = kgToGrams0(b.output_kg as string)
  const packedG = kgToGrams0(b.product_kg as string)
  const residualPct = strOrNull(b.residual_pct)
  const lossLimitPct = strOrNull(b.loss_limit_pct)
  const resBp = decimalToMinor(residualPct, 2)
  const limBp = decimalToMinor(lossLimitPct, 2)
  return {
    inputG,
    packedG,
    otherG: outputG - packedG,
    outputG,
    residualG: b.residual_kg !== undefined ? kgToGrams0(b.residual_kg as string) : inputG - outputG,
    residualPct,
    lossLimitPct,
    withinLimit: resBp === null || limBp === null ? null : Math.abs(resBp) <= limBp,
    checkedAt: ts(b.checked_at),
  }
}

export interface PackRunDetail extends PackRunRow {
  balance: RunBalance | null
}
export const mapPackRunDetail = (r: Record<string, unknown>): PackRunDetail => ({
  ...mapPackRun(r),
  balance: r.balance ? mapRunBalance(obj(r.balance)) : null,
})

export interface PackRunFilters extends PageParams {
  status?: string
  locationId?: string
  batchUnitId?: string
}
export function usePackRuns(f: PackRunFilters) {
  return usePageQuery([...pk.runs, 'list', f], () => getPage('/pack-runs', f, mapPackRun))
}
export function usePackRun(id: string | undefined) {
  return useQuery({
    queryKey: [...pk.runs, 'one', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/pack-runs/${id}`, mapPackRunDetail),
  })
}

export interface PlannedRun {
  id: string
  code: string
  plannedPouches: number | null
  plannedKg: string | null
}
export function usePlanPackRun() {
  return useConsoleMutation<ReturnType<typeof planBody>, PlannedRun>({
    mutationFn: async (body) => {
      const r = obj(await api.post('/pack-runs', body))
      return {
        id: str(r.id),
        code: str(r.code),
        plannedPouches: numOrNull(r.planned_pouches),
        plannedKg: strOrNull(r.planned_kg),
      }
    },
    invalidate: [pk.runs, pk.packable],
    success: (d) =>
      `Pack run ${d.code} planned${d.plannedPouches !== null ? ` · ${d.plannedPouches} pouches` : ''}`,
    onError: () => true,
  })
}
export function useCancelPackRun() {
  return useConsoleMutation<{ id: string; reason: string }>({
    mutationFn: ({ id, reason }) => api.post(`/pack-runs/${id}/cancel`, { reason }),
    invalidate: [pk.runs, pk.packable],
    success: 'Pack run cancelled — its kilos are free again',
    onError: () => true,
  })
}

export interface StartedRun {
  packLotId: string
  code: string
  packDate: string | null
  bestBefore: string | null
  qrUrl: string | null
  labelVersionCode: string | null
  leftoverExceptionId: string | null
}
export const mapStartedRun = (r: Record<string, unknown>): StartedRun => {
  const pl = obj(r.pack_lot)
  return {
    packLotId: str(pl.id),
    code: str(pl.code),
    packDate: strOrNull(pl.pack_date),
    bestBefore: strOrNull(pl.best_before),
    qrUrl: strOrNull(pl.qr_url),
    labelVersionCode: strOrNull(pl.label_version_code),
    leftoverExceptionId: strOrNull(r.leftover_exception_id),
  }
}
export function useStartPackRun() {
  return useConsoleMutation<{ id: string; body: ReturnType<typeof startBody> }, StartedRun>({
    mutationFn: async ({ id, body }) =>
      mapStartedRun(obj(await api.post(`/pack-runs/${id}/start`, body))),
    invalidate: [pk.runs, pk.packable, pk.lots],
    success: (d) => `Run started — pack lot ${d.code}`,
    onError: () => true,
  })
}

// ============================== pack lot (for the run page) ==============================

export interface PouchCheck {
  at: string | null
  position: string | null
  match: boolean | null
  scannedText: string | null
  photoId: string | null
}
export interface PackLotSummary {
  id: string
  code: string
  status: string
  hold: string
  packDate: string | null
  bestBefore: string | null
  qrUrl: string | null
  pouchCount: number | null
  labelsPrinted: number | null
  labelsApplied: number | null
  labelsSpoiled: number | null
  labelsDestroyed: number | null
  /** printed − (applied + spoiled + destroyed); null before the close. */
  labelGap: number | null
  reconciliationOk: boolean | null
  counterPhotoId: string | null
  pouchesRejected: number | null
  labelCode: string | null
  skuCode: string | null
  skuName: string | null
  netG: number | null
  packedG: number | null
  pouchChecks: PouchCheck[]
}

export function labelGap(
  printed: number | null,
  applied: number | null,
  spoiled: number | null,
  destroyed: number | null,
): number | null {
  if (printed === null || applied === null) return null
  return printed - (applied + (spoiled ?? 0) + (destroyed ?? 0))
}

export const mapPackLotSummary = (r: Record<string, unknown>): PackLotSummary => {
  const printed = numOrNull(r.labels_printed)
  const applied = numOrNull(r.labels_applied)
  const spoiled = numOrNull(r.labels_spoiled)
  const destroyed = numOrNull(r.labels_destroyed)
  const rec = r.reconciliation ? obj(r.reconciliation) : null
  const checks = arr(r.checks).map(obj)
  const reconciled = checks
    .filter((c) => c.biz_step === 'LABELS_RECONCILED')
    .map((c) => obj(c.payload))
  const lastRec = reconciled[reconciled.length - 1] ?? null
  return {
    id: str(r.id),
    code: str(r.code),
    status: str(r.status),
    hold: str(r.hold, 'NONE'),
    packDate: strOrNull(r.pack_date)?.slice(0, 10) ?? null,
    bestBefore: strOrNull(r.best_before)?.slice(0, 10) ?? null,
    qrUrl: strOrNull(r.qr_url),
    pouchCount: numOrNull(r.pouch_count),
    labelsPrinted: printed,
    labelsApplied: applied,
    labelsSpoiled: spoiled,
    labelsDestroyed: destroyed,
    labelGap:
      rec && rec.gap !== undefined && rec.gap !== null
        ? num(rec.gap)
        : labelGap(printed, applied, spoiled, destroyed),
    reconciliationOk: rec ? bool(rec.ok) : null,
    counterPhotoId: lastRec ? strOrNull(lastRec.counter_photo_id) : null,
    pouchesRejected: lastRec ? numOrNull(lastRec.pouches_rejected) : null,
    labelCode: strOrNull(r.label_code),
    skuCode: strOrNull(r.sku_code),
    skuName: strOrNull(r.sku_name),
    netG: numOrNull(r.net_g),
    packedG: kgToGrams(r.packed_kg as string | null),
    pouchChecks: checks
      .filter((c) => c.biz_step === 'POUCH_VERIFIED')
      .map((c) => {
        const p = obj(c.payload)
        return {
          at: ts(c.occurred_at),
          position: strOrNull(p.position),
          match: p.match === undefined ? null : bool(p.match),
          scannedText: strOrNull(p.scanned_text),
          photoId: strOrNull(p.photo_id),
        }
      }),
  }
}
export function usePackLotSummary(id: string | null) {
  return useQuery({
    queryKey: [...pk.lots, 'one', id],
    enabled: Boolean(id),
    retry: false,
    queryFn: () => getOne(`/pack-lots/${id}`, mapPackLotSummary),
  })
}

// ============================== halls and their equipment ==============================

export interface PackingHall {
  id: string
  code: string
  name: string
  fssaiLicenceNo: string | null
  fssaiLicenceValidTo: string | null
}
/** Packing halls: PACKING sites (labels and runs name the hall's site). */
export function usePackingHalls() {
  return useQuery({
    queryKey: [...pk.halls],
    staleTime: 5 * 60_000,
    queryFn: () =>
      getPage(
        '/locations',
        { kind: 'PACKING', level: 'SITE', page: 1, limit: 200 },
        mapLocation,
      ).then((p) =>
        p.rows.map((l): PackingHall => ({
          id: l.id,
          code: l.code,
          name: l.name,
          fssaiLicenceNo: l.fssaiLicenceNo,
          fssaiLicenceValidTo: l.fssaiLicenceValidTo,
        })),
      ),
  })
}

export interface HallEquipment {
  id: string
  code: string
  name: string
  isActive: boolean
  /** A registered scale or checkweigher (the start's scale check). */
  isScale: boolean
  /** Marked for the PACK step (a pack line). */
  isPackLine: boolean
  /** Allowed reading difference for the check weight (g); null = server default. */
  checkToleranceG: number | null
  calibrationDueAt: string | null
  stampDueAt: string | null
}
export const mapHallEquipment = (r: Record<string, unknown>): HallEquipment => {
  const spec = obj(r.specifications)
  const kind = typeof spec.kind === 'string' ? spec.kind.toUpperCase() : ''
  const steps = arr<unknown>(spec.step_codes).map((s) => String(s).toUpperCase())
  const tol = numOrNull(spec.check_tolerance_g)
  return {
    id: str(r.id),
    code: str(r.code),
    name: str(r.name),
    isActive: r.isActive === undefined ? true : bool(r.isActive),
    isScale: r.gateKind === 'SCALE' || kind === 'SCALE' || kind === 'CHECKWEIGHER',
    isPackLine: steps.includes('PACK'),
    checkToleranceG: tol !== null && tol >= 0 ? tol : null,
    calibrationDueAt: strOrNull(r.calibrationDueAt)?.slice(0, 10) ?? null,
    stampDueAt: strOrNull(r.stampDueAt)?.slice(0, 10) ?? null,
  }
}
export function useHallEquipment(locationId: string | null) {
  return useQuery({
    queryKey: [...pk.equipment, locationId],
    enabled: Boolean(locationId),
    staleTime: 60_000,
    queryFn: () =>
      getPage('/equipment', { locationId, page: 1, limit: 200 }, mapHallEquipment).then((p) =>
        p.rows.filter((e) => e.isActive),
      ),
  })
}
